import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, CircularProgress, useMediaQuery
} from '@mui/material';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend
} from 'chart.js';
import { BarChart2 } from 'lucide-react';
import { getDashboardStats } from '../API/bantuan';
import { getNotaList } from '../API/notaPengiriman';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const CHART_COLORS = ['#4338CA', '#10B981', '#F59E0B', '#EF4444', '#0EA5E9', '#8B5CF6', '#14B8A6'];

export const ExecutiveMonitoringCharts = () => {
  const [stats, setStats] = useState(null);
  const [notaStats, setNotaStats] = useState({ total: 0, diterima: 0, ditolak: 0, dikirim: 0 });
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width:800px)');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [dashRes, notaRes] = await Promise.all([
          getDashboardStats().catch(() => ({ data: null })),
          getNotaList({ page: 1, limit: 500 }).catch(() => ({ data: { data: [] } })),
        ]);
        setStats(dashRes.data);

        const notaRows = notaRes.data?.data || [];
        const notaCount = {
          total: notaRes.data?.totalItems ?? notaRows.length,
          diterima: notaRows.filter((n) => n.status === 'Diterima').length,
          ditolak: notaRows.filter((n) => n.status === 'Ditolak').length,
          dikirim: notaRows.filter((n) => !n.status || n.status === 'Dikirim').length,
        };
        setNotaStats(notaCount);
      } catch (err) {
        console.error('ExecutiveMonitoringCharts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const statusProses = stats?.statusProses || {};
  const statusLabels = Object.keys(statusProses);
  const statusValues = statusLabels.map((k) => Number(statusProses[k]) || 0);

  const jenisData = stats?.bantuanPerJenis || [];
  const jenisLabels = jenisData.map((j) => j.JenisBantuan || j.jenisBantuan || 'Lainnya');
  const jenisValues = jenisData.map((j) => Number(j.jumlah || j.dataValues?.jumlah) || 0);

  const notaChartData = {
    labels: ['Dikirim', 'Diterima', 'Ditolak'],
    datasets: [{
      data: [notaStats.dikirim, notaStats.diterima, notaStats.ditolak],
      backgroundColor: ['#F59E0B', '#10B981', '#EF4444'],
      borderWidth: 0,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: isMobile ? 'bottom' : 'right', labels: { boxWidth: 12, font: { size: 11 } } } },
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: isMobile ? '1rem' : '1.25rem' }}>
        <BarChart2 size={isMobile ? 18 : 22} />
        Grafik Monitoring Bantuan
      </Typography>

      <Grid container spacing={isMobile ? 2 : 3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
                Status Proses Pembangunan
              </Typography>
              <Box sx={{ height: isMobile ? 220 : 280 }}>
                {statusLabels.length > 0 ? (
                  <Bar
                    data={{
                      labels: statusLabels,
                      datasets: [{ label: 'Jumlah', data: statusValues, backgroundColor: CHART_COLORS, borderRadius: 6 }],
                    }}
                    options={{
                      ...chartOptions,
                      plugins: { ...chartOptions.plugins, legend: { display: false } },
                      scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } },
                    }}
                  />
                ) : (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Belum ada data</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
                Jenis Bantuan
              </Typography>
              <Box sx={{ height: isMobile ? 220 : 280 }}>
                {jenisLabels.length > 0 ? (
                  <Doughnut
                    data={{
                      labels: jenisLabels,
                      datasets: [{ data: jenisValues, backgroundColor: CHART_COLORS, borderWidth: 0 }],
                    }}
                    options={chartOptions}
                  />
                ) : (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Belum ada data</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
                Status Nota Pengiriman ({notaStats.total} total)
              </Typography>
              <Box sx={{ height: isMobile ? 200 : 260 }}>
                {notaStats.total > 0 ? (
                  <Doughnut data={notaChartData} options={chartOptions} />
                ) : (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Belum ada nota</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
                Ringkasan Program
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                {[
                  { label: 'Total Bantuan Aktif', value: stats?.totalBantuan || 0, color: '#4338CA' },
                  { label: 'Total Keluhan', value: stats?.totalKeluhan || 0, color: '#EF4444' },
                  { label: 'Total Nota Pengiriman', value: notaStats.total, color: '#F59E0B' },
                  { label: 'Transaksi Material', value: stats?.pembelian?.totalTransaksi || 0, color: '#10B981' },
                ].map((item) => (
                  <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ color: item.color }}>{item.value}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExecutiveMonitoringCharts;
